import React, { useState, useEffect } from 'react';
import {
    Save,
    Play,
    Check,
    Circle,
    Target,
    Clock,
    Shield,
    Mail,
    ArrowLeft,
    ArrowRight,
    Plus,
    X,
    Monitor,
    Users,
    TrendingUp,
    Globe,
    Calendar,
    Chrome
} from 'lucide-react';
import styles from './styles.module.css';

const SettingsConfig = ({ testType, formData, onUpdate, onNext, onBack }) => {
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [newReferrer, setNewReferrer] = useState('');
    const [utmKey, setUtmKey] = useState('');
    const [utmValue, setUtmValue] = useState('');

    // Default settings structure
    const defaultSettings = {
        basic: {
            trafficSplit: 50,
            duration: 14,
            sampleSize: 1000,
            confidence: 95,
            metric: 'conversion'
        },
        advanced: {
            device: 'all',
            userType: 'all',
            trafficSource: 'all',
            countries: 'all',
            timeTargeting: 'always',
            browser: 'all',
            referrers: [],
            utmParams: []
        },
        management: {
            autoStop: 'significant',
            notifications: 'milestones'
        },
        quality: {
            botFiltering: true,
            duplicateProtection: true,
            segmentOverlap: true,
            dataValidation: true
        }
    };

    // Merge existing settings with defaults
    const settings = {
        basic: { ...defaultSettings.basic, ...(formData?.settings?.basic || {}) },
        advanced: { ...defaultSettings.advanced, ...(formData?.settings?.advanced || {}) },
        management: { ...defaultSettings.management, ...(formData?.settings?.management || {}) },
        quality: { ...defaultSettings.quality, ...(formData?.settings?.quality || {}) }
    };

    // ✅ FIX: Initialize formData.settings with defaults when component mounts
    useEffect(() => {
        // If settings is null, initialize with defaults
        if (formData.settings === null || formData.settings === undefined) {
            onUpdate({ settings: defaultSettings });
        }
    }, []); // Run only once on mount

    const handleBasicUpdate = (field, value) => {
        const updatedSettings = {
            ...settings,
            basic: { ...settings.basic, [field]: value }
        };
        onUpdate({ settings: updatedSettings });
    };

    const handleAdvancedUpdate = (field, value) => {
        const updatedSettings = {
            ...settings,
            advanced: { ...settings.advanced, [field]: value }
        };
        onUpdate({ settings: updatedSettings });
    };

    const handleManagementUpdate = (field, value) => {
        const updatedSettings = {
            ...settings,
            management: { ...settings.management, [field]: value }
        };
        onUpdate({ settings: updatedSettings });
    };

    const handleQualityUpdate = (field, value) => {
        const updatedSettings = {
            ...settings,
            quality: { ...settings.quality, [field]: value }
        };
        onUpdate({ settings: updatedSettings });
    };

    const addReferrer = () => {
        if (newReferrer.trim()) {
            const updatedReferrers = [...settings.advanced.referrers, newReferrer.trim()];
            handleAdvancedUpdate('referrers', updatedReferrers);
            setNewReferrer('');
        }
    };

    const removeReferrer = (index) => {
        const updatedReferrers = settings.advanced.referrers.filter((_, i) => i !== index);
        handleAdvancedUpdate('referrers', updatedReferrers);
    };

    const addUtmParam = () => {
        if (utmKey.trim() && utmValue.trim()) {
            const updatedParams = [...settings.advanced.utmParams, { key: utmKey.trim(), value: utmValue.trim() }];
            handleAdvancedUpdate('utmParams', updatedParams);
            setUtmKey('');
            setUtmValue('');
        }
    };

    const removeUtmParam = (index) => {
        const updatedParams = settings.advanced.utmParams.filter((_, i) => i !== index);
        handleAdvancedUpdate('utmParams', updatedParams);
    };

    const handleSliderChange = (e) => {
        const value = parseInt(e.target.value);
        handleBasicUpdate('trafficSplit', value);
    };

    const handleKeyPress = (e, callback) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            callback();
        }
    };

    return (
        <div className={styles.container}>
            {/* Main Layout */}
            <div className={styles.mainLayout}>
                {/* Left Column */}
                <div className={styles.mainContent}>
                    {/* Basic Settings */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <div className={styles.cardIcon}>
                                <Circle size={24} stroke="#3b82f6" strokeWidth={2} />
                            </div>
                            <h2 className={styles.cardTitle}>Basic Settings</h2>
                        </div>

                        {/* Test Split */}
                        <div className={styles.splitContainer}>
                            <div className={styles.splitLabel}>
                                <h3>Test Split</h3>
                                <span>Traffic Distribution</span>
                            </div>
                            <div className={styles.splitVisual}>
                                <div
                                    className={styles.splitControl}
                                    style={{ flex: settings.basic.trafficSplit / 100 }}
                                >
                                    <span>{settings.basic.trafficSplit}%</span>
                                </div>
                                <div
                                    className={styles.splitVariant}
                                    style={{ flex: (100 - settings.basic.trafficSplit) / 100 }}
                                >
                                    <span>{100 - settings.basic.trafficSplit}%</span>
                                </div>
                            </div>
                            <div className={styles.splitLegends}>
                                <div className={styles.legend}>
                                    <div className={`${styles.legendDot} ${styles.legendDotBlue}`}></div>
                                    <span>Control: {settings.basic.trafficSplit}%</span>
                                </div>
                                <div className={styles.legend}>
                                    <div className={`${styles.legendDot} ${styles.legendDotGreen}`}></div>
                                    <span>Variant: {100 - settings.basic.trafficSplit}%</span>
                                </div>
                            </div>
                            <div className={styles.sliderContainer}>
                                <input
                                    type="range"
                                    className={styles.slider}
                                    min="10"
                                    max="90"
                                    value={settings.basic.trafficSplit}
                                    onChange={handleSliderChange}
                                />
                            </div>
                        </div>

                        {/* Settings Grid */}
                        <div className={styles.settingsGrid}>
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Test Duration</label>
                                <div className={styles.inputGroup}>
                                    <input
                                        type="number"
                                        className={`${styles.formInput} ${styles.inputShort}`}
                                        value={settings.basic.duration}
                                        onChange={(e) => handleBasicUpdate('duration', parseInt(e.target.value) || 14)}
                                        min="7"
                                        max="90"
                                    />
                                    <span className={styles.inputSuffix}>days</span>
                                </div>
                                <p className={styles.formHelp}>Recommended: 14-30 days</p>
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Minimal Sample Size</label>
                                <div className={styles.inputGroup}>
                                    <input
                                        type="number"
                                        className={`${styles.formInput} ${styles.inputShort}`}
                                        value={settings.basic.sampleSize}
                                        onChange={(e) => handleBasicUpdate('sampleSize', parseInt(e.target.value) || 1000)}
                                        min="100"
                                    />
                                    <span className={styles.inputSuffix}>visitors</span>
                                </div>
                                <p className={styles.formHelp}>Minimum per variant</p>
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Confidence Level</label>
                                <select
                                    className={styles.formSelect}
                                    value={settings.basic.confidence}
                                    onChange={(e) => handleBasicUpdate('confidence', parseInt(e.target.value))}
                                >
                                    <option value="90">90%</option>
                                    <option value="95">95%</option>
                                    <option value="99">99%</option>
                                </select>
                                <p className={styles.formHelp}>Statistical significance</p>
                            </div>

                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Primary Success Metric</label>
                                <select
                                    className={styles.formSelect}
                                    value={settings.basic.metric}
                                    onChange={(e) => handleBasicUpdate('metric', e.target.value)}
                                >
                                    <option value="conversion">Conversion Rate</option>
                                    <option value="ctr">Click-through Rate</option>
                                    <option value="revenue">Revenue per Visitor</option>
                                    <option value="engagement">Engagement Time</option>
                                </select>
                                <p className={styles.formHelp}>What you're optimizing for</p>
                            </div>
                        </div>
                    </div>

                    {/* Advanced Targeting Toggle */}
                    <div className={styles.advancedToggle} onClick={() => setShowAdvanced(!showAdvanced)}>
                        <div className={styles.toggleHeader}>
                            <div className={styles.toggleInfo}>
                                <div className={styles.toggleIcon}>
                                    <Target size={24} />
                                </div>
                                <div className={styles.toggleText}>
                                    <h3>Advanced Targeting</h3>
                                    <p>Specify audience segments and traffic sources</p>
                                </div>
                            </div>
                            <label className={styles.switch} onClick={(e) => e.stopPropagation()}>
                                <input
                                    type="checkbox"
                                    checked={showAdvanced}
                                    onChange={(e) => setShowAdvanced(e.target.checked)}
                                />
                                <span className={styles.switchSlider}></span>
                            </label>
                        </div>
                    </div>

                    {/* Advanced Content */}
                    {showAdvanced && (
                        <div className={`${styles.advancedContent} ${styles.advancedActive}`}>
                            <div className={styles.card}>
                                <div className={styles.targetingGrid}>
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>
                                            <Monitor size={16} style={{ display: 'inline', marginRight: '6px' }} />
                                            Target Devices
                                        </label>
                                        <select
                                            className={styles.formSelect}
                                            value={settings.advanced.device}
                                            onChange={(e) => handleAdvancedUpdate('device', e.target.value)}
                                        >
                                            <option value="all">All Devices</option>
                                            <option value="mobile">Mobile Only</option>
                                            <option value="desktop">Desktop Only</option>
                                            <option value="tablet">Tablet Only</option>
                                        </select>
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>
                                            <Users size={16} style={{ display: 'inline', marginRight: '6px' }} />
                                            User Type
                                        </label>
                                        <select
                                            className={styles.formSelect}
                                            value={settings.advanced.userType}
                                            onChange={(e) => handleAdvancedUpdate('userType', e.target.value)}
                                        >
                                            <option value="all">All Users</option>
                                            <option value="first-time">First-time</option>
                                            <option value="returning">Returning</option>
                                            <option value="logged-in">Logged-in</option>
                                            <option value="anonymous">Anonymous</option>
                                        </select>
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>
                                            <TrendingUp size={16} style={{ display: 'inline', marginRight: '6px' }} />
                                            Traffic Source
                                        </label>
                                        <select
                                            className={styles.formSelect}
                                            value={settings.advanced.trafficSource}
                                            onChange={(e) => handleAdvancedUpdate('trafficSource', e.target.value)}
                                        >
                                            <option value="all">All Traffic</option>
                                            <option value="paid">Paid Only</option>
                                            <option value="organic">Organic Only</option>
                                            <option value="direct">Direct</option>
                                            <option value="social">Social Media</option>
                                            <option value="email">Email</option>
                                        </select>
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>
                                            <Globe size={16} style={{ display: 'inline', marginRight: '6px' }} />
                                            Target Countries
                                        </label>
                                        <select
                                            className={styles.formSelect}
                                            value={settings.advanced.countries}
                                            onChange={(e) => handleAdvancedUpdate('countries', e.target.value)}
                                        >
                                            <option value="all">All Countries</option>
                                            <option value="us">United States</option>
                                            <option value="uk">United Kingdom</option>
                                            <option value="ca">Canada</option>
                                            <option value="au">Australia</option>
                                            <option value="in">India</option>
                                        </select>
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>
                                            <Calendar size={16} style={{ display: 'inline', marginRight: '6px' }} />
                                            Time Targeting
                                        </label>
                                        <select
                                            className={styles.formSelect}
                                            value={settings.advanced.timeTargeting}
                                            onChange={(e) => handleAdvancedUpdate('timeTargeting', e.target.value)}
                                        >
                                            <option value="always">Always Active</option>
                                            <option value="business">Business Hours</option>
                                            <option value="weekends">Weekends Only</option>
                                            <option value="custom">Custom Schedule</option>
                                        </select>
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>
                                            <Chrome size={16} style={{ display: 'inline', marginRight: '6px' }} />
                                            Browser Targeting
                                        </label>
                                        <select
                                            className={styles.formSelect}
                                            value={settings.advanced.browser}
                                            onChange={(e) => handleAdvancedUpdate('browser', e.target.value)}
                                        >
                                            <option value="all">All Browsers</option>
                                            <option value="chrome">Chrome Only</option>
                                            <option value="firefox">Firefox Only</option>
                                            <option value="safari">Safari Only</option>
                                            <option value="edge">Edge Only</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Referrer Sites */}
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Referrer Sites</label>
                                    <p className={styles.formHelp} style={{ marginBottom: '8px' }}>
                                        Target visitors coming from specific websites
                                    </p>
                                    <div className={styles.listInput}>
                                        <input
                                            type="text"
                                            className={styles.formInput}
                                            placeholder="e.g., google.com, facebook.com"
                                            value={newReferrer}
                                            onChange={(e) => setNewReferrer(e.target.value)}
                                            onKeyPress={(e) => handleKeyPress(e, addReferrer)}
                                        />
                                        <button
                                            className={styles.btnAdd}
                                            onClick={addReferrer}
                                            disabled={!newReferrer.trim()}
                                        >
                                            <Plus size={16} />
                                            Add
                                        </button>
                                    </div>
                                    {settings.advanced.referrers.length > 0 && (
                                        <div className={styles.tagsList}>
                                            {settings.advanced.referrers.map((referrer, index) => (
                                                <div key={index} className={styles.tag}>
                                                    <span>{referrer}</span>
                                                    <button
                                                        className={styles.tagRemove}
                                                        onClick={() => removeReferrer(index)}
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* UTM Parameters */}
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>UTM Parameters</label>
                                    <p className={styles.formHelp} style={{ marginBottom: '8px' }}>
                                        Target specific UTM campaign parameters
                                    </p>
                                    <div className={styles.utmInput}>
                                        <input
                                            type="text"
                                            className={styles.formInput}
                                            placeholder="Parameter (e.g., utm_source)"
                                            value={utmKey}
                                            onChange={(e) => setUtmKey(e.target.value)}
                                        />
                                        <input
                                            type="text"
                                            className={styles.formInput}
                                            placeholder="Value (e.g., newsletter)"
                                            value={utmValue}
                                            onChange={(e) => setUtmValue(e.target.value)}
                                            onKeyPress={(e) => handleKeyPress(e, addUtmParam)}
                                        />
                                        <button
                                            className={styles.btnAdd}
                                            onClick={addUtmParam}
                                            disabled={!utmKey.trim() || !utmValue.trim()}
                                        >
                                            <Plus size={16} />
                                            Add
                                        </button>
                                    </div>
                                    {settings.advanced.utmParams.length > 0 && (
                                        <div className={styles.tagsList}>
                                            {settings.advanced.utmParams.map((param, index) => (
                                                <div key={index} className={styles.tag}>
                                                    <span>{param.key}={param.value}</span>
                                                    <button
                                                        className={styles.tagRemove}
                                                        onClick={() => removeUtmParam(index)}
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Sidebar */}
                <div className={styles.sidebar}>
                    {/* Test Management */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <div className={styles.cardIcon}>
                                <Clock size={24} stroke="#f59e0b" strokeWidth={2} />
                            </div>
                            <h2 className={styles.cardTitle}>Test Management</h2>
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Auto-stop Test</label>
                            <select
                                className={styles.formSelect}
                                value={settings.management.autoStop}
                                onChange={(e) => handleManagementUpdate('autoStop', e.target.value)}
                            >
                                <option value="manual">Manual stop only</option>
                                <option value="significant">When significant</option>
                                <option value="duration">After duration</option>
                                <option value="sample">Sample reached</option>
                            </select>
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Email Notifications</label>
                            <select
                                className={styles.formSelect}
                                value={settings.management.notifications}
                                onChange={(e) => handleManagementUpdate('notifications', e.target.value)}
                            >
                                <option value="none">No notifications</option>
                                <option value="daily">Daily updates</option>
                                <option value="weekly">Weekly summary</option>
                                <option value="milestones">Key milestones</option>
                            </select>
                        </div>
                    </div>

                    {/* Quality Assurance */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <div className={styles.cardIcon}>
                                <Shield size={24} stroke="#10b981" strokeWidth={2} />
                            </div>
                            <h2 className={styles.cardTitle}>Quality Assurance</h2>
                        </div>

                        <div className={styles.checkboxGroup}>
                            <div className={styles.checkboxItem}>
                                <input
                                    type="checkbox"
                                    id="botFiltering"
                                    checked={settings.quality.botFiltering}
                                    onChange={(e) => handleQualityUpdate('botFiltering', e.target.checked)}
                                />
                                <label htmlFor="botFiltering">Bot traffic filtering</label>
                            </div>

                            <div className={styles.checkboxItem}>
                                <input
                                    type="checkbox"
                                    id="duplicateProtection"
                                    checked={settings.quality.duplicateProtection}
                                    onChange={(e) => handleQualityUpdate('duplicateProtection', e.target.checked)}
                                />
                                <label htmlFor="duplicateProtection">Duplicate session protection</label>
                            </div>

                            <div className={styles.checkboxItem}>
                                <input
                                    type="checkbox"
                                    id="segmentOverlap"
                                    checked={settings.quality.segmentOverlap}
                                    onChange={(e) => handleQualityUpdate('segmentOverlap', e.target.checked)}
                                />
                                <label htmlFor="segmentOverlap">Segment overlap detection</label>
                            </div>

                            <div className={styles.checkboxItem}>
                                <input
                                    type="checkbox"
                                    id="dataValidation"
                                    checked={settings.quality.dataValidation}
                                    onChange={(e) => handleQualityUpdate('dataValidation', e.target.checked)}
                                />
                                <label htmlFor="dataValidation">Real-time data validation</label>
                            </div>
                        </div>
                    </div>

                    {/* Test Summary */}
                    <div className={styles.summaryCard}>
                        <div className={styles.cardHeader}>
                            <div className={styles.cardIcon}>
                                <Mail size={24} stroke="#0c4a6e" strokeWidth={2} />
                            </div>
                            <h2 className={styles.cardTitle}>Test Summary</h2>
                        </div>

                        <div className={styles.summaryItem}>
                            <span className={styles.summaryLabel}>Expected Runtime:</span>
                            <span className={styles.summaryValue}>{settings.basic.duration} days</span>
                        </div>
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryLabel}>Min. Sample Size:</span>
                            <span className={styles.summaryValue}>{settings.basic.sampleSize.toLocaleString()} visitors</span>
                        </div>
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryLabel}>Confidence Level:</span>
                            <span className={styles.summaryValue}>{settings.basic.confidence}%</span>
                        </div>
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryLabel}>Success Metric:</span>
                            <span className={styles.summaryValue}>
                                {settings.basic.metric === 'conversion' ? 'Conversion Rate' :
                                    settings.basic.metric === 'ctr' ? 'Click-through Rate' :
                                        settings.basic.metric === 'revenue' ? 'Revenue per Visitor' :
                                            'Engagement Time'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className={styles.footer}>
                {/* <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={onBack}>
                    <ArrowLeft size={16} />
                    Back
                </button> */}
                <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={onNext}>
                    Save & Next
                    <ArrowRight size={16} />
                </button>
            </div>
        </div>
    );
};

export default SettingsConfig;